import { Tooltip } from "@/components/generic/Tooltip";
import { Company, Participant } from "@/types/company";
import { getRole } from "@/utils/virk-utils";
import { TooltipTrigger } from "react-aria-components";
import { FocusableText } from "../generic/FocusableText";
import { Heading } from "../generic/Heading";

interface RelationsProps {
  board?: Company["board"];
  ownership?: Company["ownership"];
  founders?: Company["founders"];
  management?: Company["management"];
  stakeholders?: Company["stakeholders"];
}

export const Relations = ({
  board,
  ownership,
  management,
  stakeholders,
}: RelationsProps) => {
  // Helper to render person with tooltip and ellipsis
  const renderPerson = (person: Participant) => {
    const role = getRole(person);
    const fullText = `${person.name}${role ? ` (${role})` : ""}`;

    return (
      <li className="text-xs" key={person.lassoId}>
        <FocusableText
          className="h-full truncate overflow-x-hidden"
          text={fullText}
        />
      </li>
    );
  };

  // Process and render stakeholders
  const renderStakeholders = () => {
    if (!stakeholders || stakeholders.length === 0) return null;

    return (
      <div className="relation-group flex flex-col gap-y-4">
        <Heading level={3} className="text-xs font-bold">
          Interessenter
        </Heading>
        <ul className="flex flex-col gap-y-2">
          {stakeholders.map(renderPerson)}
        </ul>
      </div>
    );
  };

  // Process and render board members
  const renderBoard = () => {
    if (!board) return null;

    const allMembers: Participant[] = [];
    if (board.chairman) allMembers.push(board.chairman);
    if (board.members) allMembers.push(...board.members);
    if (board.alternates) allMembers.push(...board.alternates);

    if (allMembers.length === 0) return null;

    return (
      <div className="relation-group flex flex-col gap-y-4">
        <Heading level={3} className="text-xs font-bold">
          Bestyrelse
        </Heading>
        <ul className="flex flex-col gap-y-2">
          {allMembers.map(renderPerson)}
        </ul>
      </div>
    );
  };

  // Process and render ownership
  const renderOwnership = () => {
    if (!ownership || !ownership.owners || ownership.owners.length === 0)
      return null;

    return (
      <div className="relation-group flex flex-col gap-y-4">
        <Heading level={3} className="text-xs font-bold">
          Ejere
        </Heading>
        <ul className="flex flex-col gap-y-2">
          {ownership.owners.map(renderPerson)}
        </ul>
      </div>
    );
  };

  // Process and render management
  const renderManagement = () => {
    if (!management) return null;

    const allMembers: Participant[] = [];
    if (management.ceo) allMembers.push(management.ceo);
    if (management.members) allMembers.push(...management.members);

    if (allMembers.length === 0) return null;

    return (
      <div className="relation-group flex flex-col gap-y-4">
        <Heading level={3} className="text-xs font-bold">
          Direktion
        </Heading>
        <ul className="flex flex-col gap-y-2">
          {allMembers.map(renderPerson)}
        </ul>
      </div>
    );
  };

  // Check if any relations exist
  const hasRelations =
    (management &&
      (management.ceo ||
        (management.members && management.members.length > 0))) ||
    (board &&
      (board.chairman ||
        (board.members && board.members.length > 0) ||
        (board.alternates && board.alternates.length > 0))) ||
    (ownership && ownership.owners && ownership.owners.length > 0) ||
    (stakeholders && stakeholders.length > 0);

  return (
    <div className="flex w-full flex-col gap-y-8">
      {hasRelations ? (
        <>
          {renderManagement()}
          {renderBoard()}
          {renderOwnership()}
          {renderStakeholders()}
        </>
      ) : (
        <div className="text-theme-on-background-variant text-sm">
          Ingen relationer fundet
        </div>
      )}
    </div>
  );
};
